import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { PermissionSlug } from '../constants/featureFlags';
import { getAgencyConfig } from '../services/agencyConfig';
import { useStore } from '../store';

/**
 * Whether a person may not change a visit's location of care and HCPCS.
 * `holdsTaskDetailsEdit` already counts a super user as a holder. Mirrors
 * `billing_codes_locked_for` in the backend's app/services/visit_task_details.py.
 */
export function billingCodesLockedFor(
  settingOn: boolean,
  isOasis: boolean,
  holdsTaskDetailsEdit: boolean
): boolean {
  return settingOn && !isOasis && !holdsTaskDetailsEdit;
}

/**
 * Whether the visit form shows the location of care and HCPCS read-only.
 *
 * Reports `false` until the agency config resolves, and on failure: most
 * agencies keep the lock off, and the server refuses a locked change anyway.
 */
export function useVisitNoteBillingCodesLocked(isOasis: boolean): boolean {
  const agencyId = useStore(useShallow(s => s.user?.agency?.id));
  const holdsTaskDetailsEdit = useStore(s =>
    s.hasPermission(PermissionSlug.TASK_DETAILS_EDIT)
  );
  const [settingOn, setSettingOn] = useState(false);
  const needsSetting = !!agencyId && !isOasis && !holdsTaskDetailsEdit;

  useEffect(() => {
    if (!needsSetting) {
      setSettingOn(false);
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const cfg = await getAgencyConfig();
        if (!cancelled) {
          setSettingOn(Boolean(cfg.lock_visit_note_billing_codes));
        }
      } catch (error) {
        console.error('Failed to load the billing code lock setting:', error);
        if (!cancelled) setSettingOn(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [agencyId, needsSetting]);

  return billingCodesLockedFor(settingOn, isOasis, holdsTaskDetailsEdit);
}
